import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, SignIn } from '@phosphor-icons/react';
import ExYuMap from '../components/ExYuMap';
import { useLang } from '../contexts/LanguageContext';

/**
 * A/B test variant of LandingPage — side-by-side Continue + Log In buttons,
 * instead of the "Already have an account? Sign in" underline link.
 * Route: /login-button-test (preview only, not linked from navigation).
 */
export default function LandingPageLoginButtonTest() {
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();
  const { t } = useLang();

  const handleContinue = () => {
    if (selected) {
      navigate('/onboarding', { state: { heritage: selected } });
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{
        backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/30562ac1-6cb8-40db-b89b-be786ceb71ff/images/b6e93548f6284f1e6ff426330f7c166e29890a4ff25926fc64207adc64cd1a22.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="landing-page-variant"
    >
      <div className="absolute inset-0 bg-[#060A1A]/85" />

      {/* Small "A/B TEST" badge so you know which variant you're on */}
      <span
        className="absolute top-4 right-4 z-20 text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] bg-[#060A1A]/70 backdrop-blur-sm"
        data-testid="variant-badge"
      >
        Variant B · Login Button
      </span>

      <motion.div
        className="relative z-10 text-center max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-center gap-3 mb-6">
          <Heart weight="fill" size={36} className="text-[#D4AF37]" />
          <h1 className="text-5xl md:text-6xl font-light tracking-tight text-white font-['Outfit']">
            Yu<span className="text-gold-gradient font-bold">Blink</span>
          </h1>
        </div>

        <p className="text-[#9EABCE] text-base md:text-lg mb-2 font-['Manrope']">
          {t('landing_tagline')}
        </p>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-10">
          {t('select_heritage')}
        </p>

        <ExYuMap onSelect={setSelected} selected={selected} />

        {/* Side-by-side button row */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          {/* Primary: Continue */}
          <motion.button
            onClick={handleContinue}
            disabled={!selected}
            data-testid="landing-continue-btn-variant"
            className={`inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl text-base font-bold transition-all duration-300 w-full sm:w-auto ${
              selected
                ? 'btn-gold'
                : 'bg-white/5 text-[#9EABCE]/40 cursor-not-allowed border border-white/5'
            }`}
            whileHover={selected ? { scale: 1.03 } : {}}
            whileTap={selected ? { scale: 0.98 } : {}}
          >
            {t('continue')}
            <ArrowRight size={20} weight="bold" />
          </motion.button>

          {/* Secondary: Log In (ghost-gold outline) */}
          <motion.button
            onClick={() => navigate('/auth')}
            data-testid="landing-login-btn-variant"
            className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl text-base font-semibold transition-all duration-300 w-full sm:w-auto border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] bg-[#060A1A]/30 backdrop-blur-sm"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <SignIn size={20} weight="bold" />
            Log In
          </motion.button>
        </div>

        {/* Preserve helpful hint for first-time visitors */}
        <p className="mt-5 text-[11px] text-[#9EABCE]/50">
          New here? Select your heritage and tap <span className="text-[#D4AF37]">Continue</span> ·
          Already a member? Tap <span className="text-[#D4AF37]">Log In</span>
        </p>
      </motion.div>
    </div>
  );
}
